import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppButton,
  AppCard,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppSpinner,
} from '../../../components/ui';
import secteursAdminService from '../../../services/secteursAdminService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';

const EMPTY = { nomSecteur: '', nomCommercial: '', couleurSecteur: '#007bff' };

const SecteursAdminSecteurFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    secteursAdminService.getSecteur(id)
      .then((s) => setForm({
        nomSecteur: s.nomSecteur || '',
        nomCommercial: s.nomCommercial || '',
        couleurSecteur: s.couleurSecteur || '#007bff',
      }))
      .catch((err) => {
        setLoadError(err.message);
        notify.error(err.message);
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await secteursAdminService.updateSecteur(id, form);
        notify.success('Secteur enregistré');
      } else {
        await secteursAdminService.createSecteur(form);
        notify.success('Secteur créé');
      }
      navigate(ROUTES.secteurs.adminSecteurs);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title={isEdit ? 'Modifier le secteur' : 'Nouveau secteur'}
        actions={(
          <AppButton as={Link} to={ROUTES.secteurs.adminSecteurs} size="sm" variant="outline-secondary">Retour</AppButton>
        )}
      />

      {loadError ? (
        <AppEmptyState message="Secteur introuvable" />
      ) : (
        <AppCard>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <AppFormField className="col-md-6" label="Nom du secteur" htmlFor="secteur-nom" required>
                <AppInput
                  id="secteur-nom"
                  type="text"
                  required
                  value={form.nomSecteur}
                  onChange={(e) => setForm((f) => ({ ...f, nomSecteur: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Nom commercial" htmlFor="secteur-commercial" required>
                <AppInput
                  id="secteur-commercial"
                  type="text"
                  required
                  value={form.nomCommercial}
                  onChange={(e) => setForm((f) => ({ ...f, nomCommercial: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Couleur" htmlFor="secteur-couleur">
                <div className="d-flex gap-2 align-items-center">
                  <AppInput
                    id="secteur-couleur"
                    type="color"
                    className="form-control-color"
                    value={form.couleurSecteur}
                    onChange={(e) => setForm((f) => ({ ...f, couleurSecteur: e.target.value }))}
                  />
                  <span className="text-secondary-light">{form.couleurSecteur}</span>
                </div>
              </AppFormField>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <AppButton as={Link} to={ROUTES.secteurs.adminSecteurs} variant="secondary">Annuler</AppButton>
              <AppButton type="submit" variant="success" loading={saving} loadingLabel="Enregistrement…">
                Enregistrer
              </AppButton>
            </div>
          </form>
        </AppCard>
      )}
    </MasterLayout>
  );
};

export default SecteursAdminSecteurFormPage;
